import { useQuery } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { planKeys } from '~/lib/query-keys';

export type PlanStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'cancelled'
  | 'completed'
  | 'superseded';

/** What a plan does on approval (FRONT-289). `write_to_graph` plans run inline
 *  and, once completed, render as a "Saved to your knowledge graph" card;
 *  `background_execute` plans act in the background (FRONT-495). */
export type PlanKind = 'research' | 'write_to_graph' | 'background_execute';

/** FRONT-3031: one kind of action (a Policy row) the plan may take without a card. */
export interface PlanCoveredAction {
  domain: string;
  action: string;
  /** The Policy page's name for the row. */
  name: string;
  /** The row was at Needs approval when the plan was proposed — only these are worth listing. */
  asks: boolean;
}

export interface Plan {
  /** Persistent DB id (FRONT-652). Sent back on a decision so the backend acts
   *  on the exact plan the card rendered (the stale-card guard). */
  id: number;
  /** FRONT-652: the resource this plan acts on; '' = no specific target. */
  target: string;
  title: string;
  context: string;
  steps: string[];
  verification: string[];
  outOfScope: string[];
  status: PlanStatus;
  kind: PlanKind;
  rejectReason?: string;
  createdAt: string;
  decidedAt?: string;
  /** FRONT-569: set once the user dismisses the completed "Saved to your
   *  knowledge graph" card. When present, the card stays hidden across reopens. */
  dismissedAt?: string;
  /** FRONT-3031: what approving this plan lets Eddy do without asking. */
  coveredActions?: PlanCoveredAction[];
}

interface PlanResponse {
  plan: Plan | null;
}

export function usePlan(sessionId: string | undefined) {
  return useQuery({
    queryKey: sessionId ? planKeys.bySession(sessionId) : planKeys.all,
    queryFn: async () => {
      if (!sessionId) return null;
      const data = await api.get<PlanResponse>(`/api/plan/current?sessionId=${encodeURIComponent(sessionId)}`);
      return data.plan;
    },
    enabled: !!sessionId,
    // FRONT-230: the global staleTime is 60s, so without this a reopen within
    // that window won't refetch and a still-pending plan card stays hidden.
    // Force a fresh /api/plan/current pull on every mount so reopening the
    // inbox modal (or the main chat) re-shows the pending plan.
    refetchOnMount: 'always',
  });
}
